import { PropsWithChildren, createContext, useCallback, useState } from 'react';
import { AutocompletePrediction } from '@bridebook/web/app-shared/lib/search/types';

export interface IGooglePredictionsContext {
  predictions: AutocompletePrediction[];
  googlePredictions: { [key: string]: google.maps.places.AutocompletePrediction };
  predictionsLocation: string;
  setPredictions(
    predictions: IGooglePredictionsContext['predictions'],
    googlePredictions: IGooglePredictionsContext['googlePredictions'],
    predictionsLocation?: string,
  ): void;
}

const initialContext: IGooglePredictionsContext = {
  predictions: [],
  googlePredictions: {},
  predictionsLocation: '',
  setPredictions: (): void => {
    throw new Error('setPredictions function must be overridden');
  },
};

/*
 * Save Google autocomplete predictions in react context to pass between components
 * To save data use this:
 * import { GooglePredictionsContext } from '@bridebook/web/components/search/location-autocomplete/context';
 * const { setPredictions } = useContext(GooglePredictionsContext);
 * setPredictions(...)
 *  ---
 * To retrieve data use this:
 * import { GooglePredictionsContext } from '@bridebook/web/components/search/location-autocomplete/context';
 * const { predictions } = useContext(GooglePredictionsContext);
 * console.log(predictions);
 */
export const GooglePredictionsContext = createContext<IGooglePredictionsContext>(initialContext);

// this is a workaround to avoid initializing another instance of Google places api

const GooglePredictionsContextProvider = ({ children }: PropsWithChildren<{}>) => {
  const [predictions, setPredictions] = useState<{
    predictions: IGooglePredictionsContext['predictions'];
    googlePredictions: IGooglePredictionsContext['googlePredictions'];
    predictionsLocation: string;
  }>({
    predictions: initialContext.predictions,
    googlePredictions: initialContext.googlePredictions,
    predictionsLocation: initialContext.predictionsLocation,
  });

  const setPredictionCallback = useCallback(
    (
      predictions: IGooglePredictionsContext['predictions'],
      googlePredictions: IGooglePredictionsContext['googlePredictions'],
      predictionsLocation: IGooglePredictionsContext['predictionsLocation'],
    ) => {
      setPredictions({
        predictions,
        googlePredictions,
        predictionsLocation,
      });
      return;
    },
    [],
  );

  const context = { ...predictions, setPredictions: setPredictionCallback };

  return (
    <GooglePredictionsContext.Provider value={context}>
      {children}
    </GooglePredictionsContext.Provider>
  );
};

export default GooglePredictionsContextProvider;
